import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

/**
 * Runtime Tamper Detection Service
 * 
 * Detects if critical browser APIs or application state have been tampered with.
 * Protects against:
 * - Prototype pollution attacks
 * - Monkey-patching of fetch/XMLHttpRequest
 * - Object.defineProperty attacks on auth state
 * - localStorage manipulation of security-critical values
 * 
 * Only active in production mode.
 */
@Injectable({
  providedIn: 'root'
})
export class TamperDetectionService {

  private isActive = false;
  private checkIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly CHECK_INTERVAL_MS = 15_000; // Check every 15 seconds

  // Store original references to detect monkey-patching
  private originalFetch: typeof fetch | null = null;
  private originalXhrOpen: Function | null = null;
  private originalLocalStorageSetItem: Function | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  /**
   * Start tamper detection monitoring.
   * Call once from AppComponent.
   */
  activate(): void {
    if (!environment.production || this.isActive) return;
    this.isActive = true;

    // Store original references
    this.originalFetch = window.fetch;
    this.originalXhrOpen = XMLHttpRequest.prototype.open;
    this.originalLocalStorageSetItem = Storage.prototype.setItem;

    // Freeze critical objects to prevent prototype pollution
    this.freezeCriticalPrototypes();

    // Start periodic integrity checks
    this.ngZone.runOutsideAngular(() => {
      this.checkIntervalId = setInterval(() => {
        this.runIntegrityChecks();
      }, this.CHECK_INTERVAL_MS);
    });
  }

  /**
   * Freeze prototypes of critical objects to prevent pollution.
   */
  private freezeCriticalPrototypes(): void {
    try {
      // Freeze Object.prototype against additions (prevents __proto__ pollution)
      Object.freeze(Object.prototype);
    } catch {
      // Some environments don't allow this — that's OK
    }
  }

  /**
   * Run integrity checks on critical browser APIs.
   */
  private runIntegrityChecks(): void {
    const violations: string[] = [];

    // Check if fetch has been monkey-patched
    if (this.originalFetch && window.fetch !== this.originalFetch) {
      violations.push('fetch_tampered');
    }

    // Check if XMLHttpRequest.open has been replaced
    if (this.originalXhrOpen && XMLHttpRequest.prototype.open !== this.originalXhrOpen) {
      violations.push('xhr_tampered');
    }

    // Check if critical document properties are intact
    if (typeof document.cookie === 'undefined') {
      violations.push('cookie_access_blocked');
    }

    // Check for suspicious global variables (common in XSS payloads)
    const suspiciousGlobals = ['__malware', '__payload', '__hook', '_injected'];
    for (const g of suspiciousGlobals) {
      if ((window as any)[g] !== undefined) {
        violations.push(`suspicious_global_${g}`);
      }
    }

    // If violations detected, log and potentially force logout
    if (violations.length > 0) {
      this.handleTamperDetected(violations);
    }
  }

  private handleTamperDetected(violations: string[]): void {
    // Log the violation
    try {
      const events = JSON.parse(sessionStorage.getItem('security_events') || '[]');
      events.push({
        type: 'TAMPER_DETECTED',
        violations,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
      sessionStorage.setItem('security_events', JSON.stringify(events));
    } catch { /* storage disabled */ }

    // For critical violations (fetch/XHR tampered), force logout
    const criticalViolations = ['fetch_tampered', 'xhr_tampered'];
    const hasCritical = violations.some(v => criticalViolations.includes(v));

    if (hasCritical && this.authService.isAuthenticated) {
      this.ngZone.run(() => {
        this.authService.logout();
        this.router.navigate(['/login'], {
          queryParams: { reason: 'security_violation' }
        });
      });
    }
  }

  /**
   * Stop monitoring (for cleanup).
   */
  deactivate(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    this.isActive = false;
  }
}
